import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { issueTrackerApi, Issue } from '../services/issueTrackerApi';
import { aiAssistantApi, DocumentMatch, EmailDraft } from '../services/aiAssistantApi';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  FileText,
  RefreshCw,
  Ban,
  User,
  Calendar,
  ChevronRight,
  Sparkles,
  Mail,
  Link2,
  Copy,
  Wand2,
  FileSearch
} from 'lucide-react';
import { toast } from 'sonner';

// Status configuration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'pending_review': { label: 'Pending Review', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200', icon: Clock },
  'approved': { label: 'Approved', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200', icon: CheckCircle },
  'rejected': { label: 'Rejected', color: 'text-gray-700', bgColor: 'bg-gray-50 border-gray-200', icon: XCircle },
  'sent_to_client': { label: 'Sent to Client', color: 'text-purple-700', bgColor: 'bg-purple-50 border-purple-200', icon: Send },
  'resolved': { label: 'Resolved', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: CheckCircle },
  'wont_fix': { label: "Won't Fix", color: 'text-gray-500', bgColor: 'bg-gray-100 border-gray-300', icon: Ban }
};

// Priority configuration
const PRIORITY_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  'critical': { label: 'Critical', color: 'text-red-700', bgColor: 'bg-red-100' },
  'high': { label: 'High', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  'medium': { label: 'Medium', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'low': { label: 'Low', color: 'text-gray-600', bgColor: 'bg-gray-100' }
};

// Issue type labels
const ISSUE_TYPE_LABELS: Record<string, string> = {
  'missing_document': 'Missing Document',
  'data_inconsistency': 'Data Inconsistency',
  'calculation_variance': 'Calculation Variance',
  'causation_unclear': 'Unclear Causation',
  'documentation_gap': 'Documentation Gap',
  'policy_coverage_question': 'Policy Coverage Question'
};

export default function IssueDetail() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Action states
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // AI Assistant states
  const [documentMatches, setDocumentMatches] = useState<DocumentMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [confirmingMatch, setConfirmingMatch] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  useEffect(() => {
    if (issueId) {
      fetchIssue();
    }
  }, [issueId]);

  const fetchIssue = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await issueTrackerApi.getIssue(issueId!);
      setIssue(data);
    } catch (err: any) {
      console.error('Failed to fetch issue:', err);
      setError(err.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!issue) return;
    
    try {
      setApproving(true);
      const updated = await issueTrackerApi.approveIssue(issue.id, 'current-user');
      setIssue(updated);
      toast.success('Issue approved successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve issue');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!issue || !rejectReason.trim()) return;
    
    try {
      setRejecting(true);
      const updated = await issueTrackerApi.rejectIssue(issue.id, 'current-user', rejectReason);
      setIssue(updated);
      setShowRejectModal(false);
      setRejectReason('');
      toast.success('Issue rejected');
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject issue');
    } finally {
      setRejecting(false);
    }
  };

  // AI Assistant: Find matching documents
  const handleFindDocuments = async () => {
    if (!issue) return;
    
    try {
      setLoadingMatches(true);
      const result = await aiAssistantApi.matchDocuments(issue.id);
      setDocumentMatches(result.matches);
      if (result.matches.length === 0) {
        toast.info('No matching documents found');
      } else {
        toast.success(`Found ${result.matches.length} potential matches`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to find documents');
    } finally {
      setLoadingMatches(false);
    }
  };

  // AI Assistant: Confirm document match
  const handleConfirmMatch = async (documentId: string, documentName: string) => {
    if (!issue) return;
    
    try {
      setConfirmingMatch(documentId);
      // Resolve the issue with the matched document
      await issueTrackerApi.resolveIssue(
        issue.id,
        `Resolved with document: ${documentName}`,
        documentId
      );
      toast.success('Issue resolved with matched document!');
      // Refresh issue to get updated status
      fetchIssue();
      // Clear matches since issue is resolved
      setDocumentMatches([]);
    } catch (err: any) {
      toast.error(err.message || 'Failed to confirm match');
    } finally {
      setConfirmingMatch(null);
    }
  };

  // AI Assistant: Reject document match (just removes from list)
  const handleRejectMatch = (documentId: string) => {
    setDocumentMatches(prev => prev.filter(m => m.document_id !== documentId));
    toast.info('Match dismissed');
  };

  // AI Assistant: Draft client email
  const handleDraftEmail = async () => {
    if (!issue) return;
    
    try {
      setLoadingEmail(true);
      const result = await aiAssistantApi.draftEmail(
        [issue.id],
        'Client', // Would come from report data
        issue.report_id.slice(0, 8).toUpperCase()
      );
      setEmailDraft(result.draft);
      setShowEmailModal(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to draft email');
    } finally {
      setLoadingEmail(false);
    }
  };

  const copyEmailToClipboard = () => {
    if (!emailDraft) return;
    const fullEmail = `Subject: ${emailDraft.subject}\n\n${emailDraft.body}`;
    navigator.clipboard.writeText(fullEmail);
    toast.success('Email copied to clipboard');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
          <span className="ml-2 text-gray-600">Loading issue...</span>
        </div>
      </AppLayout>
    );
  }

  if (error || !issue) {
    return (
      <AppLayout>
        <div className="p-6 max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/issues')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Issues
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h2 className="text-lg font-medium text-red-900 mb-2">Issue Not Found</h2>
            <p className="text-red-700">{error || 'The requested issue could not be found.'}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusConfig = STATUS_CONFIG[issue.status] || STATUS_CONFIG['pending_review'];
  const priorityConfig = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG['medium'];
  const StatusIcon = statusConfig.icon;
  const canTakeAction = issue.status === 'pending_review';

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl mx-auto" data-testid="issue-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/issues')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-to-issues-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Issues
        </button>

        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                {priorityConfig.label}
              </span>
              <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full border ${statusConfig.bgColor}`}>
                <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                <span className={`text-sm font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
              </span>
            </div>
            
            <div className="text-xs text-gray-400">
              ID: {issue.id.slice(0, 12)}...
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">{issue.title}</h1>
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              Detected by {issue.detected_by}
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(issue.created_at)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{issue.description}</p>
            </div>

            {/* What Would Fix */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-brand-primary" />
                <h2 className="font-semibold text-gray-900">Recommended Resolution</h2>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{issue.what_would_fix}</p>
            </div>

            {/* AI Document Matches */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-purple-600" />
                  <h2 className="font-semibold text-gray-900">AI Document Matcher</h2>
                </div>
                <button
                  onClick={handleFindDocuments}
                  disabled={loadingMatches}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  data-testid="find-documents-btn"
                >
                  {loadingMatches ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSearch className="w-4 h-4" />
                  )}
                  Find Documents
                </button>
              </div>
              
              {documentMatches.length > 0 ? (
                <div className="space-y-3">
                  {documentMatches.map((match, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-purple-100">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-gray-900">{match.document_name}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          match.match_confidence >= 0.8 ? 'bg-green-100 text-green-700' :
                          match.match_confidence >= 0.6 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {(match.match_confidence * 100).toFixed(0)}% match
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{match.relevance_explanation}</p>
                      <p className="text-xs text-purple-600 font-medium mb-3">{match.suggested_action}</p>
                      
                      {/* Confirm/Reject buttons */}
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handleConfirmMatch(match.document_id, match.document_name)}
                          disabled={confirmingMatch === match.document_id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                          data-testid={`confirm-match-${idx}`}
                        >
                          {confirmingMatch === match.document_id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Resolve with this
                        </button>
                        <button
                          onClick={() => handleRejectMatch(match.document_id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 text-sm hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`reject-match-${idx}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Click "Find Documents" to search for documents that might resolve this issue
                </p>
              )}
            </div>

            {/* Status History */}
            {issue.status_history && issue.status_history.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Status History</h2>
                <div className="space-y-4">
                  {issue.status_history.map((change, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-gray-300 mt-2" />
                      <div>
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{change.changed_by}</span>
                          {' changed status from '}
                          <span className="font-medium">{STATUS_CONFIG[change.from_status]?.label || change.from_status}</span>
                          {' to '}
                          <span className="font-medium">{STATUS_CONFIG[change.to_status]?.label || change.to_status}</span>
                        </div>
                        {change.reason && (
                          <p className="text-sm text-gray-500 mt-1">Reason: {change.reason}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{formatDate(change.changed_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution (if resolved) */}
            {issue.resolution && (
              <div className="bg-green-50 rounded-lg border border-green-200 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h2 className="font-semibold text-green-900">Resolution</h2>
                </div>
                <p className="text-green-800">{issue.resolution}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            {canTakeAction && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Actions</h2>
                <div className="space-y-3">
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="approve-issue-btn"
                  >
                    {approving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Approve Issue
                  </button>
                  
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    data-testid="reject-issue-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject Issue
                  </button>
                </div>
              </div>
            )}

            {/* AI Email Drafter */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-blue-600" />
                <h2 className="font-semibold text-gray-900">AI Email Drafter</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Generate a professional email to request the missing documentation from the client.
              </p>
              <button
                onClick={handleDraftEmail}
                disabled={loadingEmail}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                data-testid="draft-email-btn"
              >
                {loadingEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                Draft Email
              </button>
            </div>

            {/* Details */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Details</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs text-gray-500 uppercase">Case ID</dt>
                  <dd className="text-sm text-gray-900 font-mono">{issue.report_id}</dd>
                </div>
                {issue.affected_amount && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase">Affected Amount</dt>
                    <dd className="text-sm text-orange-600 font-semibold">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(issue.affected_amount)}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs text-gray-500 uppercase">Created</dt>
                  <dd className="text-sm text-gray-900">{formatDate(issue.created_at)}</dd>
                </div>
                {issue.updated_at && (
                  <div>
                    <dt className="text-xs text-gray-500 uppercase">Last Updated</dt>
                    <dd className="text-sm text-gray-900">{formatDate(issue.updated_at)}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Related</h2>
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/report/${issue.report_id}`)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    View Case Report
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Issue</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting this issue.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary resize-none"
                rows={4}
                data-testid="reject-reason-input"
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-reject-btn"
                >
                  {rejecting ? 'Rejecting...' : 'Reject Issue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Draft Modal */}
        {showEmailModal && emailDraft && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">AI-Generated Email Draft</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  emailDraft.urgency === 'HIGH' ? 'bg-red-100 text-red-700' :
                  emailDraft.urgency === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {emailDraft.urgency} Priority
                </span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Subject</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 font-medium">
                    {emailDraft.subject}
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-500 uppercase mb-1">Body</label>
                  <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-700 whitespace-pre-wrap text-sm">
                    {emailDraft.body}
                  </div>
                </div>

                {emailDraft.deadline_suggestion && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">Suggested deadline:</span> {emailDraft.deadline_suggestion}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={copyEmailToClipboard}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="copy-email-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
